import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shell, Disclaimer } from "@/components/Marketing";
import { ClauseWizard } from "@/components/ClauseWizard";
import { api } from "@/lib/api";
import { ArrowRight, BookOpen, Scale, Coins, GitCompare, Landmark, HelpCircle, Check, X } from "lucide-react";

const Wrap = ({ children }) => <Shell><article className="max-w-3xl mx-auto px-5 pt-14 pb-8">{children}</article></Shell>;
const H1 = ({ children }) => <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] leading-tight">{children}</h1>;
const H2 = ({ children }) => <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0B132B] mt-10 mb-3">{children}</h2>;
const P = ({ children }) => <p className="mt-3 text-[#2A3441] leading-relaxed">{children}</p>;
const CTA = ({ label = "Start your agreement" }) => <div className="mt-10"><Link to="/register" className="inline-flex items-center gap-2 bg-[#0B132B] text-white px-6 py-3 rounded-full font-medium">{label} <ArrowRight className="w-4 h-4" /></Link><span className="ml-3 text-sm text-[#5C6B73]">$18.99/mo · 7-day free trial</span></div>;

const CARDS = [
  { to: "/learn/clauses", Icon: Scale, t: "Clause Library & Safety Check", d: "Every clause you can include, what you can't, and a wizard that flags risky terms for your state." },
  { to: "/learn/alimony", Icon: Coins, t: "Alimony / Spousal Support", d: "What it is, the types, and how a prenup lets you cap, waive, or restructure it." },
  { to: "/learn/prenup-vs-postnup", Icon: GitCompare, t: "Prenup vs Postnup", d: "The key differences in timing, scrutiny, and enforceability." },
  { to: "/learn/postnup", Icon: BookOpen, t: "Postnup Guide", d: "Purpose, history, when to sign, and where postnups may not be enforceable." },
  { to: "/learn/upmaa", Icon: Landmark, t: "UPAA / UPMAA", d: "The uniform acts, which states adopted them, and how state rules still differ." },
  { to: "/learn/faq", Icon: HelpCircle, t: "Prenup FAQ", d: "Straight answers to the questions couples ask most." },
];

export function LearnHub() {
  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-5 pt-14 pb-8">
        <H1>Learn</H1>
        <p className="mt-4 text-lg text-[#5C6B73]">Plain-English guides to prenups and postnups, written to help you make confident, informed decisions.</p>

        <div className="mt-8 bg-white border border-[#E2E8F0] rounded-2xl p-6">
          <h2 className="font-display text-2xl font-bold text-[#0B132B]">What exactly is a prenup?</h2>
          <p className="mt-3 text-[#2A3441] leading-relaxed">A prenuptial agreement (or premarital agreement) is a contract between two people who are about to get married. It must be signed before the wedding day to be valid. It sets out the terms you both agree to in the event of a divorce (and sometimes death) and details the assets you hold together and individually.</p>
          <p className="mt-3 text-[#2A3441] leading-relaxed">A prenup is also a financial-planning tool: it gives you and your future spouse a structured way to talk about how assets and debt will be handled during the marriage. Terms can cover assets, debt, alimony, inheritances, gifts, taxes, joint accounts, pet custody, and much more.</p>
          <p className="mt-3 text-[#2A3441] leading-relaxed">Every state has its own default divorce laws that decide how property is divided. A prenup lets a couple override those defaults and decide what feels right for <em>them</em>.</p>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 gap-5">
          {CARDS.map((c) => (
            <Link key={c.to} to={c.to} data-testid={`learn-card-${c.to.split("/").pop()}`} className="bg-white border border-[#E2E8F0] rounded-xl p-6 hover:-translate-y-0.5 transition-transform">
              <c.Icon className="w-5 h-5 text-[#4A6FA5] mb-3" />
              <div className="font-semibold text-[#0B132B]">{c.t}</div>
              <p className="text-sm text-[#5C6B73] mt-1">{c.d}</p>
            </Link>
          ))}
        </div>
        <div className="mt-8"><Disclaimer /></div>
        <CTA />
      </div>
    </Shell>
  );
}

export function ClauseLibrary() {
  const [clauses, setClauses] = useState([]);
  useEffect(() => { api.get("/legal/clauses").then((r) => setClauses(r.data.clauses)).catch(() => {}); }, []);
  const allowed = clauses.filter((c) => c.category !== "Restricted");
  const restricted = clauses.filter((c) => c.category === "Restricted");
  const cats = [...new Set(allowed.map((c) => c.category))];
  return (
    <Shell>
      <div className="max-w-4xl mx-auto px-5 pt-14 pb-8">
        <H1>Clause Library & Safety Check</H1>
        <p className="mt-4 text-lg text-[#5C6B73]">A prenup is bespoke — one size does not fit all. Here's what you can include, what you can't, and a wizard to flag risky terms for your state.</p>

        {cats.map((cat) => (
          <div key={cat} className="mt-8">
            <div className="text-xs font-semibold uppercase tracking-wide text-[#4A6FA5] mb-2">{cat}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {allowed.filter((c) => c.category === cat).map((c) => (
                <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
                  <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /><span className="font-semibold text-[#0B132B] text-sm">{c.name}</span></div>
                  <p className="text-sm text-[#5C6B73] mt-1">{c.summary}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        <H2>What you can't include</H2>
        <div className="grid sm:grid-cols-2 gap-3">
          {restricted.map((c) => (
            <div key={c.id} className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2"><X className="w-4 h-4 text-red-600" /><span className="font-semibold text-[#0B132B] text-sm">{c.name}</span></div>
              <p className="text-sm text-[#5C6B73] mt-1">{c.summary}</p>
            </div>
          ))}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4"><div className="flex items-center gap-2"><X className="w-4 h-4 text-red-600" /><span className="font-semibold text-[#0B132B] text-sm">Divorce incentives</span></div><p className="text-sm text-[#5C6B73] mt-1">Terms that reward divorce are against public policy and won't be enforced.</p></div>
        </div>

        <H2>Check your clauses</H2>
        <ClauseWizard />
        <div className="mt-8"><Disclaimer /></div>
        <CTA />
      </div>
    </Shell>
  );
}

export function AlimonyGuide() {
  return (
    <Wrap>
      <H1>Alimony & Spousal Support</H1>
      <P>Alimony (also called spousal support or spousal maintenance) is financial support paid by one spouse to the other after divorce. Its purpose is to help maintain the lifestyle established during the marriage and to prevent one spouse from being left in a far worse financial position.</P>
      <P>Picture the classic "homemaker and breadwinner." If one spouse left the workforce for twenty years to raise a family, a court may require the higher-earning spouse to pay support so the lower-earning spouse isn't stranded. Real life is rarely that black-and-white, and the types and durations of alimony vary by state.</P>
      <H2>How a prenup changes the picture</H2>
      <P>With a prenup, you and your partner have nearly total control over alimony: set an amount, set caps, structure it around milestones, or waive it entirely. A prenup lets you contract around your state's default formula and design something that fits you both — which can protect a lower-earning spouse just as easily as a higher-earning one.</P>
      <H2>Important caveats</H2>
      <P>Waivers aren't absolute. Some states won't enforce a waiver that leaves a spouse destitute, and support terms are weighed when a prenup's fairness is challenged (for example, if circumstances changed so much that enforcing a total waiver would be unconscionable). In California, a spousal-support waiver generally requires that the waiving party had independent counsel at signing.</P>
      <div className="mt-8"><Disclaimer /></div>
      <CTA />
    </Wrap>
  );
}

export function PrenupVsPostnup() {
  const rows = [
    ["When it's signed", "Before the wedding day", "Any time during the marriage"],
    ["Topics covered", "Property, debt, support, estate", "The same topics"],
    ["Court scrutiny", "Often less scrutiny", "Often more scrutiny (varies by state)"],
    ["Deadline", "Must be signed before marriage", "No deadline"],
    ["Availability", "Enforceable in every state", "Not enforceable in a few states"],
  ];
  return (
    <Wrap>
      <H1>Prenup vs. Postnup</H1>
      <P>Prenuptial and postnuptial agreements address the same things — property division, debt allocation, spousal support, and estate matters. The main differences are <strong>when</strong> they're signed and <strong>how</strong> they're scrutinized.</P>
      <div className="mt-6 overflow-hidden rounded-xl border border-[#E2E8F0]">
        <table className="w-full text-sm">
          <thead className="bg-[#0B132B] text-white"><tr><th className="text-left p-3"> </th><th className="text-left p-3">Prenup</th><th className="text-left p-3">Postnup</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 ? "bg-[#F8F9FA]" : "bg-white"}><td className="p-3 font-medium text-[#0B132B]">{r[0]}</td><td className="p-3 text-[#5C6B73]">{r[1]}</td><td className="p-3 text-[#5C6B73]">{r[2]}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-8"><Disclaimer /></div>
      <CTA />
    </Wrap>
  );
}

export function PostnupGuide() {
  return (
    <Wrap>
      <H1>Postnuptial Agreements</H1>
      <P>The main purpose of a postnuptial agreement is to protect each spouse's financial interests in the event of a divorce. It creates a roadmap — agreed while you're still married and on good terms — for how assets and debt would be divided, which can make any future divorce faster, cheaper, and less painful. A postnup can also simply provide financial clarity and alignment, such as when one spouse leaves a career to raise children.</P>
      <H2>A little history</H2>
      <P>Postnups in the United States date back to the 1800s. One early case upholding a postnup came from North Carolina in 1828 (Liles v. Fleming), where the court enforced a post-nuptial agreement "made upon sufficient consideration, between husband and wife." The Married Women's Property Acts (starting in Mississippi in 1839 and New York in 1848) recognized married women as separate legal persons who could own property and contract — laying the groundwork for marital agreements, even as courts stayed cautious for decades.</P>
      <H2>When to initiate one</H2>
      <P>Unlike prenups, postnups have no deadline. You can sign one a day after the wedding or twenty years in — as long as you and your spouse aren't already planning to divorce.</P>
      <H2>Where postnups may not be enforceable</H2>
      <P>In most states, postnups are enforceable if you follow the legal requirements. But there are exceptions. Postnups are generally not enforceable in <strong>Nebraska, Oklahoma, and Iowa</strong>. In <strong>New Jersey and Indiana</strong>, only "reconciliation" postnups (where there was a genuine marital breakdown that led to the agreement) are recognized — a happy couple generally can't get an enforceable postnup in those states.</P>
      <H2>Typical requirements</H2>
      <P>Requirements vary, but commonly include: the agreement is in writing and signed by both parties; entered into voluntarily; supported by adequate legal consideration; backed by full financial disclosure; each spouse had independent counsel or the chance to hire one; and the terms are reasonably fair. Some states also require witnesses and notarization.</P>
      <div className="mt-8"><Disclaimer /></div>
      <CTA />
    </Wrap>
  );
}

export function UPMAAGuide() {
  const [states, setStates] = useState([]);
  useEffect(() => { api.get("/legal/states").then((r) => setStates(r.data.states.filter((s) => s.adopted))).catch(() => {}); }, []);
  return (
    <Wrap>
      <H1>UPAA & UPMAA</H1>
      <P>The <strong>Uniform Premarital Agreement Act (UPAA)</strong> was drafted in 1983 by the Uniform Law Commission (the ULC) to bring consistency to widely differing state prenup laws — so that, for example, an agreement signed in one state is more likely to be honored if you move to another.</P>
      <P>In 2012 the ULC updated it as the <strong>Uniform Premarital and Marital Agreements Act (UPMAA)</strong>, adding clearer procedural and substantive safeguards and — as the name suggests — extending coverage to marital (postnuptial) agreements.</P>
      <H2>What the UPMAA established</H2>
      <ul className="mt-3 space-y-2 list-disc pl-5 text-[#2A3441]">
        <li>Agreements must be in writing, and are enforceable without separate consideration.</li>
        <li>An agreement that was unconscionable when signed can be refused enforcement.</li>
        <li>Failure to provide adequate financial disclosure is a ground for refusing enforcement.</li>
        <li>Agreements signed involuntarily (duress) are unenforceable.</li>
        <li>Terms limiting remedies for domestic violence are unenforceable.</li>
        <li>Parties may specify which state's law governs the agreement.</li>
      </ul>
      <H2>States that adopted a version</H2>
      <P>Colorado and North Dakota led the way in 2013; today a version of the UPAA/UPMAA has been adopted (often with modifications) across many states and DC:</P>
      <div className="mt-3 flex flex-wrap gap-2">
        {states.map((s) => <Link key={s.code} to={`/state-laws/${s.name.toLowerCase().replace(/ /g, "-")}`} className="text-xs rounded-full px-3 py-1 border border-[#E2E8F0] text-[#0B132B] hover:border-[#4A6FA5]">{s.name}</Link>)}
      </div>
      <H2>State rules still differ</H2>
      <P>Even among adopting states, the details vary. Florida, for instance, requires a death/elective-share waiver to be signed before two witnesses. Nevada requires "fair and reasonable" financial disclosure and makes agreements easier to challenge. That's why NuPDocs pairs your draft with state-specific, sourced rules — and flags terms that may not hold up where you live.</P>
      <div className="mt-8"><Disclaimer /></div>
      <CTA />
    </Wrap>
  );
}

const FAQS = [
  ["Is a prenup good or bad?", "For most couples, good. A prenup protects financial and business interests and — just as importantly — gives you both a structured way to align on money, property, inheritance, and support. It can protect the less-wealthy spouse too."],
  ["Who benefits from a prenup?", "Both of you. Prenups aren't just for the wealthy — they're worth considering for anyone with earning potential, assets, debt, a business, children from a prior relationship, or a future inheritance. Almost every couple benefits from the planning conversation."],
  ["Do prenups cost both spouses money?", "They can, and most couples agree to each pay for themselves. Attorney-drafted prenups often run $1,200–$2,400+ per person, with retainers around $2,500 each and complex matters reaching $7,500–$10,000. NuPDocs lets couples create and negotiate a draft together in hours for a fraction of that — and you can still take the draft to an attorney."],
  ["Does cheating void a prenup?", "No. Adultery or abuse doesn't invalidate a prenup unless the agreement specifically says so. You can add an infidelity clause, but they aren't always enforceable — a California court found one unenforceable as contrary to no-fault divorce policy (Diosdado v. Diosdado, 2002)."],
  ["Does a prenup protect future earnings?", "It can. You can define how income earned during the marriage is treated and whether spousal support applies — within your state's limits."],
  ["Should I be offended by a prenup?", "Try not to be. A prenup is a planning tool and an invitation to get on the same page about money — not a prediction of failure."],
  ["How long before a wedding should you get a prenup?", "As early as reasonably possible. Signing well before the wedding avoids any appearance of pressure, and some states (like California) impose a review period before signing."],
  ["Can a prenup be 'broken'?", "It can be challenged — usually for involuntary signing (duress), lack of financial disclosure, or unconscionability. Doing it right, with full disclosure and independent counsel, makes it far more durable."],
  ["Are marriages with prenups more likely to fail?", "No — the planning conversations a prenup requires tend to strengthen communication about money, one of the most common sources of marital stress."],
];

export function LearnFAQ() {
  return (
    <Wrap>
      <H1>Prenup FAQ</H1>
      <div className="mt-6 space-y-3">
        {FAQS.map(([q, a], i) => (
          <div key={i} className="border border-[#E2E8F0] rounded-xl p-5 bg-white" data-testid={`learn-faq-${i}`}>
            <div className="font-semibold text-[#0B132B]">{q}</div>
            <p className="text-sm text-[#5C6B73] mt-1 leading-relaxed">{a}</p>
          </div>
        ))}
      </div>
      <div className="mt-8"><Disclaimer /></div>
      <CTA />
    </Wrap>
  );
}
